import React from 'react';
import { Button, Dropdown, Tooltip, useToasts } from 'freemium-ui';
import { Trans, useTranslation } from 'react-i18next';
import { AccountUserType, ConfirmationType } from 'shared/types';
import { useAccessControl } from 'utils/access-control';
import Icon from '../../../../../../shared/components/Icon';
import {
  useDeleteAccount,
  useGetCurrentAccountUser,
  useGetCurrentUser,
  useTransferOwnership,
} from '../../../hooks';
import { useGetAllUsers } from '../../user/hooks';
import { DrawerFields, Message } from '../styles';
import AccountAPIKey from './account-api-key';
import EmailHashKey from './account-email-hash';

type AccountActionsProps = {
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
};

const AccountActions = (props: AccountActionsProps) => {
  const { t } = useTranslation();
  const { setConfirmationData } = props;
  const { access } = useAccessControl();
  const [admins, setAdmins] = React.useState<any>([]);
  const [accountAdmin, setAccountAdmin] = React.useState<any>(null);
  const [selectedAdmin, setSelectedAdmin] = React.useState<any>(null);

  const [, setToast] = useToasts();
  const { data: activeUsers } = useGetAllUsers({});
  const { data: loggedInUser } = useGetCurrentUser({});
  const { isFetching: isFetchingCurrentAccountUser } = useGetCurrentAccountUser(
    loggedInUser?.display_id || '',
  );

  const { mutate: tranferOwnership } = useTransferOwnership({
    setToast,
    callback: (isSuccess: boolean) => {
      if (isSuccess) {
        setConfirmationData({});
      } else {
        setConfirmationData((old) => ({
          ...old,
          confirmButtonDisabled: false,
        }));
      }
    },
  });
  const { mutate: deleteAccount } = useDeleteAccount({
    setToast,
    callback: (isSuccess: boolean) => {
      if (isSuccess) {
        setConfirmationData({});
      } else {
        setConfirmationData((old) => ({
          ...old,
          confirmButtonDisabled: false,
        }));
      }
    },
  });

  const hasOwnerAccess = React.useMemo(
    () => (access || []).includes('account-transfer:write'),
    [access],
  );
  const hasAdminAccess = React.useMemo(
    () => (access || []).includes('settings:read'),
    [access],
  );

  React.useEffect(() => {
    if (activeUsers?.account_users?.length) {
      const temp = activeUsers.account_users.filter(
        (a: AccountUserType) =>
          a.user_role === 'ADMIN' && a?.user?.status === 'ACTIVATED',
      );
      setAccountAdmin(
        activeUsers?.account_users.find((a: any) => a.user_role === 'OWNER'),
      );
      setAdmins(temp);
    }
  }, [activeUsers, loggedInUser]);

  return (
    <>
      <DrawerFields>
        <div className="item">
          <div className="key">
            {t('settings.account_info.sidebar.plan.title')}
          </div>
          <div className="value">
            {t('settings.account_info.sidebar.plan.value')}
          </div>
        </div>
        <div className="item">
          <div className="key">
            <span>
              {t('settings.account_info.sidebar.account_data_center')}
            </span>
            <Tooltip label={`${t('tooltip.account_data')}`} position="top">
              <Icon glyph="info" width="12" height="12" />
            </Tooltip>
          </div>
          <div className="value">
            {t('settings.account_info.sidebar.region')}
          </div>
        </div>
        <div className="item">
          <div className="key">
            {t('settings.user_management.fields.roleOwner.name')}
          </div>
          <div className="value">{accountAdmin?.user?.email}</div>
        </div>
        {hasAdminAccess ? (
          <>
            <div className="item">
              <AccountAPIKey />
            </div>
            <div className="item">
              <EmailHashKey />
            </div>
          </>
        ) : null}
        {hasOwnerAccess && !isFetchingCurrentAccountUser && (
          <div className="item">
            <div className="key">
              <span>{t('settings.account_info.sidebar.admin.ownership')}</span>
              <Tooltip
                label={`${t('tooltip.transfer_ownership')}`}
                position="top"
              >
                <Icon glyph="info" width="12" height="12" />
              </Tooltip>
            </div>
            <div>
              <Dropdown
                isRequired
                defaultSelectedItem={selectedAdmin}
                itemToString={(v: any) => v.user.email}
                itemComponent={(v: any) => v.user.email}
                emptyItemsText={`${t('no_results_found')}`}
                disabled={admins?.length === 0}
                items={admins}
                onChange={(option: any) => {
                  if (!option) return;
                  setSelectedAdmin(option);
                }}
                placeholder={`${t(
                  'settings.account_info.sidebar.admin.select_admin',
                )}`}
              />
              {selectedAdmin && (
                <Button
                  inline
                  disabled={!selectedAdmin}
                  className="transfer-button"
                  onClick={() => {
                    setConfirmationData({
                      id: new Date().toISOString(),
                      title: `${t('settings.transfer_ownership_confirmation')}`,
                      className: 'error',
                      confirmButtonText: `${t('confirm')}`,
                      onConfirm: () => {
                        setConfirmationData((old) => ({
                          ...old,
                          confirmButtonDisabled: true,
                        }));
                        tranferOwnership(selectedAdmin?.user?.display_id);
                      },
                      fill: '#d7461f',
                      description: (
                        <Message>
                          <Trans
                            i18nKey="settings.confirm_transfer"
                            values={{
                              email: selectedAdmin?.user?.email,
                            }}
                            components={{ bold: <b /> }}
                          />
                        </Message>
                      ),
                      glyph: 'info',
                    });
                  }}
                >
                  {t('settings.account_info.sidebar.admin.transfer')}
                </Button>
              )}
            </div>
          </div>
        )}
        {hasOwnerAccess && (
          <Button
            className="custom-delete-btn"
            type="link"
            onClick={() => {
              setConfirmationData({
                id: new Date().toISOString(),
                title: `${t('confirm_deletion')}`,
                className: 'error',
                confirmButtonText: 'Delete',
                onConfirm: () => {
                  setConfirmationData((old) => ({
                    ...old,
                    confirmButtonDisabled: true,
                  }));
                  deleteAccount();
                },
                fill: '#d7461f',
                infoText: 'This action is irreversible',
                description: 'Are you sure to delete this Account',
                glyph: 'info',
              });
            }}
          >
            <Icon glyph="delete" width="12" height="12" fill="none" />
            &nbsp;{t('settings.account_info.sidebar.delete_account_text')}
          </Button>
        )}
      </DrawerFields>
    </>
  );
};

export default AccountActions;
